import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import FileViewer from "@/components/custom/FileViewer";
import { caseEventApi } from "@/api/events";
import { useReadApi } from "@/hooks/useReadApi";
import { CaseEvent } from "@/api/events";
import { useTranslation } from "react-i18next";
import { ResourceInfoModal } from "@/components/custom/ResourceInfoModal";
import sanitizeHtml from "sanitize-html";
import { Button } from "@/components/ui/button";

export default function EventSourceViewer() {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const [openInfo, setOpenInfo] = useState(false);

  const pageFrom = parseInt(searchParams.get("pageFrom") || "1");
  const pageTo = parseInt(searchParams.get("pageTo") || "1");

  const { loading, apiResult: eventData } = useReadApi<CaseEvent>(
    caseEventApi,
    eventId,
  );

  if (loading) {
    return <div>{t("common.button.loading")}</div>;
  }

  if (!eventData) {
    return (
      <div className="text-center text-2xl font-bold">
        {t("events.statictext.event_not_found")}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{eventData.title}</h1>
      </div>
      <div className="flex w-full justify-end">
        <Button onClick={() => setOpenInfo(true)} className="mb-4">
          {t("common.button.view_info")}
        </Button>
      </div>
      <ResourceInfoModal
        open={openInfo}
        setOpen={setOpenInfo}
        title={t("events.title.event")}
        mappings={{
          data: [
            {
              label: t("common.label.title"),
              value: eventData?.title || "--",
            },
            {
              label: t("common.label.author"),
              value: eventData?.author || "--",
            },
            {
              label: t("common.label.date"),
              value: eventData?.date || "--",
            },
            {
              label: t("common.label.description"),
              value: sanitizeHtml(eventData?.description || "--"),
            },
            {
              label: t("common.label.source"),
              value: eventData?.source?.title || "--",
            },
            {
              label: t("events.label.case_tags"),
              value:
                eventData?.tags?.map((item) => item.name).join(", ") || "--",
            },
            {
              label: t("common.label.start_page"),
              value: eventData?.starting_page || "--",
            },
            {
              label: t("common.label.end_page"),
              value: eventData?.ending_page || "--",
            },
            {
              label: t("events.label.include_in_timeline"),
              value:
                eventData?.include_in_timeline.toString() === "true"
                  ? t("common.values.yes")
                  : t("common.values.no"),
            },
          ],
        }}
      />

      {eventData?.source?.file ? (
        <div
          className="w-full"
          style={{
            border: "1px solid rgba(0, 0, 0, 0.3)",
            height: window.innerHeight / 1.3,
          }}
        >
          <FileViewer
            fileUrl={eventData.source?.file as string}
            pageFrom={pageFrom}
            pageTo={pageTo}
          />
        </div>
      ) : (
        <div className="text-center text-2xl font-bold">
          {t("sources.statictext.source_file_not_found")}
        </div>
      )}
    </div>
  );
}
